/**
 * ET Cart Page — upsell sliders (Swiper 11 · EcomTraffic · Finex / Impact 7.2.0)
 * Owner: ET - Cart Upsells (sections/et-cart-page-upsells.liquid, snippets/et-cart-page-upsell-card.liquid).
 *
 * Markup contract
 *   Root      [data-et-cart-upsells][data-section-id][data-mode-desktop="stack|slider"][data-mode-mobile="stack|slider"]
 *   Viewport  [data-et-cart-upsells-viewport] > .et-cart-upsells__track > .et-cart-upsells__slide[data-et-cart-upsell-block]
 *   Scrollbar [data-et-cart-upsells-scrollbar] > .et-cart-upsells__scrollbar-thumb   (Swiper Scrollbar module, draggable)
 *
 * The mode is picked per breakpoint (matchMedia 749px): "slider" → Swiper (≥750px: 2 per view, 1 when the slider is narrower
 * than 540px, e.g. in the cart sidebar; below 750px: 'auto' 280px slides),
 * "stack" → plain CSS grid (no Swiper). The instance is destroyed and re-created on DOMContentLoaded, shopify:section:load,
 * et-cart:section-replaced (AJAX cart refresh) and breakpoint changes; the active slide is kept across a live re-render.
 * shopify:section:unload destroys; shopify:block:select slides to the selected block.
 */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-et-cart-upsells]';
  const SLIDE_SELECTOR = '.et-cart-upsells__slide';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const SPACE_BETWEEN = 16;
  const NARROW_WIDTH = 540; // desktop/tablet: 1 slide per view below this slider width (sidebar), 2 from it — matches the CSS container query
  // Figma indicator 54443:188805: 55px thumb in the 96px inner track. The section publishes both numbers (the indicator
  // settings) as data-thumb-min / data-thumb-track and feeds the same pair to the CSS thumb width, so the two agree.
  const DEFAULT_THUMB_MIN = 55;
  const DEFAULT_THUMB_TRACK = 96;

  const instances = new Map(); // sectionId → { root, mobile, swiper, frame }
  const savedIndexes = new Map(); // sectionId → active index to restore after a re-render

  const isMobile = () => MOBILE_QUERY.matches;
  const sectionIdOf = (root) => root.getAttribute('data-section-id') || '';

  function wantsSlider(root) {
    if (!root.isConnected || root.classList.contains('et-cart-page-section--hidden')) return false;
    return root.getAttribute(isMobile() ? 'data-mode-mobile' : 'data-mode-desktop') === 'slider';
  }

  function destroyInstance(sectionId, keepIndex) {
    const entry = instances.get(sectionId);
    if (!entry) return;
    instances.delete(sectionId);
    if (entry.frame) window.cancelAnimationFrame(entry.frame);
    if (!entry.swiper || entry.swiper.destroyed) return;
    if (keepIndex) savedIndexes.set(sectionId, entry.swiper.activeIndex || 0);
    entry.swiper.destroy(true, true);
  }

  /** Thumb floor as a fraction of the track, from the section's indicator settings (same pair the CSS divides). */
  function thumbRatioOf(root) {
    const min = parseFloat(root.getAttribute('data-thumb-min'));
    const track = parseFloat(root.getAttribute('data-thumb-track'));
    if (!isFinite(min) || min < 0 || !isFinite(track) || track <= 0) return DEFAULT_THUMB_MIN / DEFAULT_THUMB_TRACK;
    return Math.min(1, min / track);
  }

  /** Swiper sizes the thumb to the visible fraction; keep it at least the section's floor of the track, like the Figma. */
  function sizeThumb(entry) {
    const swiper = entry ? entry.swiper : null;
    const scrollbar = swiper && !swiper.destroyed ? swiper.scrollbar : null;
    if (!scrollbar || !scrollbar.el || !swiper.virtualSize) return;

    const track = scrollbar.el.offsetWidth;
    if (!track) return;

    const natural = (track * swiper.size) / swiper.virtualSize;
    const size = Math.round(Math.min(track, Math.max(natural, track * entry.thumbRatio)));
    if (swiper.params.scrollbar.dragSize === size) return;

    swiper.params.scrollbar.dragSize = size;
    scrollbar.updateSize();
    scrollbar.setTranslate();
  }

  function scheduleThumb(entry) {
    if (entry.frame) window.cancelAnimationFrame(entry.frame);
    entry.frame = window.requestAnimationFrame(() => {
      entry.frame = 0;
      sizeThumb(entry);
    });
  }

  function initRoot(root) {
    const sectionId = sectionIdOf(root);
    if (!sectionId) return;

    const current = instances.get(sectionId);
    const slider = wantsSlider(root);
    if (slider && current && current.root === root && current.mobile === isMobile() && current.swiper && !current.swiper.destroyed) return;
    if (current) destroyInstance(sectionId, true);
    if (!slider || typeof window.Swiper !== 'function') return;

    const viewport = root.querySelector('[data-et-cart-upsells-viewport]');
    const slides = viewport ? viewport.querySelectorAll(SLIDE_SELECTOR) : [];
    if (!viewport || !slides.length) return;

    const mobile = isMobile();
    const initialSlide = Math.min(savedIndexes.get(sectionId) || 0, slides.length - 1);
    savedIndexes.delete(sectionId);

    const entry = { root, mobile, swiper: null, frame: 0, thumbRatio: thumbRatioOf(root) };
    const refreshThumb = () => scheduleThumb(entry);
    const options = {
      wrapperClass: 'et-cart-upsells__track',
      slideClass: 'et-cart-upsells__slide',
      slidesPerView: mobile ? 'auto' : 1,
      spaceBetween: SPACE_BETWEEN,
      initialSlide,
      watchOverflow: true,
      a11y: { enabled: true, notificationClass: 'et-cart-upsells__notification' },
      on: {
        init: refreshThumb,
        resize: refreshThumb,
        breakpoint: refreshThumb,
        update: refreshThumb,
        slidesUpdated: refreshThumb,
        lock: refreshThumb,
        unlock: refreshThumb
      }
    };

    // Desktop/tablet: slides per view follow the slider's own width (main column vs. the 420px sidebar), not the viewport
    if (!mobile) {
      options.breakpointsBase = 'container';
      options.breakpoints = { [NARROW_WIDTH]: { slidesPerView: 2 } };
    }

    const scrollbarEl = root.querySelector('[data-et-cart-upsells-scrollbar]');
    if (scrollbarEl) {
      options.scrollbar = {
        el: scrollbarEl,
        draggable: true,
        snapOnRelease: true,
        dragClass: 'et-cart-upsells__scrollbar-thumb',
        lockClass: 'et-cart-upsells__scrollbar-bar--lock',
        horizontalClass: 'et-cart-upsells__scrollbar-bar--horizontal',
        verticalClass: 'et-cart-upsells__scrollbar-bar--vertical',
        scrollbarDisabledClass: 'et-cart-upsells__scrollbar-bar--disabled'
      };
    }

    // Only register the entry once Swiper constructed successfully, so a failed init never leaves a null instance behind.
    let swiper = null;
    try {
      swiper = new window.Swiper(viewport, options);
    } catch (error) {
      if (entry.frame) window.cancelAnimationFrame(entry.frame);
      console.error('[ET cart upsells] Swiper init failed', error);
      return;
    }
    entry.swiper = swiper;
    instances.set(sectionId, entry);
  }

  function destroyDetached() {
    instances.forEach((entry, sectionId) => {
      if (!entry.root.isConnected) destroyInstance(sectionId, true);
    });
  }

  function rootsIn(scope) {
    const context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    const roots = Array.from(context.querySelectorAll(ROOT_SELECTOR));
    if (context instanceof Element && context.matches(ROOT_SELECTOR)) roots.unshift(context);
    return roots;
  }

  function initAll(scope) {
    destroyDetached();
    rootsIn(scope).forEach(initRoot);
  }

  function onSectionReplaced(event) {
    const element = event.detail && event.detail.element;
    if (element instanceof Element && (element.matches(ROOT_SELECTOR) || element.querySelector(ROOT_SELECTOR))) initAll(element);
    else destroyDetached();
  }

  function onSectionUnload(event) {
    rootsIn(event.target).forEach((root) => {
      const sectionId = sectionIdOf(root);
      destroyInstance(sectionId, false);
      savedIndexes.delete(sectionId);
    });
  }

  function onBlockSelect(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-et-cart-upsell-block]') : null;
    const root = slide ? slide.closest(ROOT_SELECTOR) : null;
    if (!root) return;

    initRoot(root);
    const entry = instances.get(sectionIdOf(root));
    if (!entry || entry.root !== root || !entry.swiper || entry.swiper.destroyed) return;

    const index = entry.swiper.slides.indexOf(slide);
    if (index > -1) entry.swiper.slideTo(index);
  }

  const onBreakpointChange = () => initAll(document);
  if (typeof MOBILE_QUERY.addEventListener === 'function') MOBILE_QUERY.addEventListener('change', onBreakpointChange);
  else if (typeof MOBILE_QUERY.addListener === 'function') MOBILE_QUERY.addListener(onBreakpointChange);

  document.addEventListener('et-cart:section-replaced', onSectionReplaced);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:select', onBlockSelect);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
})();
